import { useState, useCallback } from 'react';
import { AssetType, AssetDimensions } from '../types';
import { getAssetProperties, track } from 'analytics/posthog';

const MAX_IMAGE_SIZE = 3 * 1024 * 1024; // 3 MB
const MAX_VIDEO_SIZE = 30 * 1024 * 1024; // 30 MB

export interface UseFileUploadReturn {
  file: File | null;
  preview: string | null;
  dimensions: AssetDimensions | null;
  handleFile: (uploadedFile: File, type: AssetType) => void;
  handleFileUpload: (
    e: React.ChangeEvent<HTMLInputElement>,
    type: AssetType,
  ) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>, type: AssetType) => void;
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleRemove: () => void;
  setUploadState: (
    file: File | null,
    preview: string | null,
    dimensions: AssetDimensions | null,
  ) => void;
  reset: () => void;
}

export const useFileUpload = (): UseFileUploadReturn => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState<AssetDimensions | null>(null);

  const handleFile = useCallback((uploadedFile: File, type: AssetType) => {
    const isImage = uploadedFile.type.startsWith('image/');
    const isVideo = uploadedFile.type.startsWith('video/');
    const limit = isImage ? MAX_IMAGE_SIZE : MAX_VIDEO_SIZE;

    if (uploadedFile.size > limit) {
      alert(
        `${isImage ? 'Image' : 'Video'} exceeds size limit of ${
          isImage ? '3MB' : '30MB'
        }`,
      );
      return;
    }

    setFile(uploadedFile);
    track('ondemand asset selected', {
      ...getAssetProperties({
        file: uploadedFile,
        dimensions: null,
        assetRole: type,
      }),
      surface: 'ondemand',
    });

    const reader = new FileReader();
    reader.onload = e => {
      const result = e.target?.result as string;

      if (isImage) {
        const img = new Image();
        img.onload = () => {
          const nextDimensions = { width: img.width, height: img.height };
          setDimensions(nextDimensions);
          track('ondemand asset selected', {
            ...getAssetProperties({
              file: uploadedFile,
              dimensions: nextDimensions,
              assetRole: type,
            }),
            surface: 'ondemand',
          });
        };
        img.src = result;
      }

      if (isVideo) {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {

          // Capture both spatial dimensions and duration for videos
          setDimensions({
            width: video.videoWidth,
            height: video.videoHeight,
            duration: parseFloat(video.duration.toFixed(2)),
          });
          track('ondemand asset selected', {
            ...getAssetProperties({
              file: uploadedFile,
              dimensions: {
                width: video.videoWidth,
                height: video.videoHeight,
                duration: parseFloat(video.duration.toFixed(2))
              },
              assetRole: type,
            }),
            surface: 'ondemand',
          });
        };
        video.src = result;
      }

      setPreview(result);
    };
    reader.readAsDataURL(uploadedFile);
  }, []);

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, type: AssetType) => {
      const uploadedFile = e.target.files?.[0];
      if (uploadedFile) handleFile(uploadedFile, type);
    },
    [handleFile],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, type: AssetType) => {
      e.preventDefault();
      const uploadedFile = e.dataTransfer.files?.[0];
      if (uploadedFile) handleFile(uploadedFile, type);
    },
    [handleFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleRemove = useCallback(() => {
    setFile(null);
    setPreview(null);
    setDimensions(null);
  }, []);

  const setUploadState = useCallback(
    (
      nextFile: File | null,
      nextPreview: string | null,
      nextDimensions: AssetDimensions | null,
    ) => {
      setFile(nextFile);
      setPreview(nextPreview);
      setDimensions(nextDimensions);
    },
    [],
  );

  const reset = useCallback(() => {
    setFile(null);
    setPreview(null);
    setDimensions(null);
  }, []);

  return {
    file,
    preview,
    dimensions,
    handleFile,
    handleFileUpload,
    handleDrop,
    handleDragOver,
    handleRemove,
    setUploadState,
    reset,
  };
};
